import React, { useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Switch,
  Button,
  IconButton,
  Avatar,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import CommandPalette from './CommandPalette';
import { ColorModeContext } from '../App';
import { useTheme } from '@mui/material/styles';

const drawerWidth = 240;

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const { logout, user } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    if (path === '#') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
      setMobileOpen(false);
      return;
    }
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Grouped Menu Items: All original links are preserved!
  const homeItems = [
    { text: 'Search (Ctrl+K)', icon: <SearchIcon />, path: '#' },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Students', icon: <PeopleIcon />, path: '/users' },
    { text: 'Applications', icon: <AssignmentIcon />, path: '/applications' },
    { text: 'Scholarships', icon: <SchoolIcon />, path: '/scholarships' },
    { text: 'Challans', icon: <ReceiptIcon />, path: '/challans' },
    { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
    { text: 'Telemarketing', icon: <PhoneIcon />, path: '/tele-challan' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  ];

  const configItems = [
    { text: 'Users & Roles', icon: <SecurityIcon />, path: '/roles' },
    { text: 'System Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'HunarmandSindh PSIDs', icon: <ReceiptIcon />, path: '/digisindh-psids' },
    { text: 'Edit Records', icon: <EditIcon />, path: '/edit' },
    { text: 'Audit Logs', icon: <HistoryIcon />, path: '/audit-logs' },
    { text: 'Manage Courses', icon: <SchoolIcon />, path: '/courses' },
  ];

  const renderNavItem = (item: any) => {
    const isActive = location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/');
    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => handleMenuClick(item.path)}
          sx={{
            px: 3,
            py: 1,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '10%',
              height: '80%',
              width: '3px',
              backgroundColor: '#059669',
              borderRadius: '0 4px 4px 0',
              opacity: isActive ? 1 : 0,
              transition: 'opacity 0.2s',
            },
            '&:hover': {
              bgcolor: 'transparent',
              '& .MuiListItemIcon-root': { color: '#059669' },
              '& .MuiListItemText-primary': { color: '#059669' }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#059669' : '#a3aed1' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: isActive ? 700 : 500,
              color: isActive ? '#059669' : '#a3aed1',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{
      height: '100%',
      bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '4px 0 20px rgba(0,0,0,0.02)',
    }}>
      {/* Logo Area */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 3, mt: 1 }}>
        <img src="/logo.png" alt="HS Logo" style={{ width: 40, height: 40, marginRight: 16, objectFit: 'contain' }} />
        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#2b3674', lineHeight: 1.2 }}>HS Admin</Typography>
          <Typography sx={{ fontSize: '12px', color: '#a3aed1', fontWeight: 500 }}>Panel</Typography>
        </Box>
      </Box>

      {/* Menu List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, mt: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
        <Typography sx={{ px: 3, mb: 1, fontSize: '12px', fontWeight: 700, color: '#2b3674' }}>Home</Typography>
        <List sx={{ pt: 0 }}>
          {homeItems.map(renderNavItem)}

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton sx={{ px: 3, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40, color: '#a3aed1' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Light Mode"
                primaryTypographyProps={{ fontSize: '13px', fontWeight: 500, color: '#a3aed1' }}
              />
              <Switch 
                checked={theme.palette.mode === 'light'} 
                onChange={colorMode.toggleColorMode}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#059669' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#059669' },
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Typography sx={{ px: 3, mb: 1, mt: 2, fontSize: '12px', fontWeight: 700, color: '#2b3674' }}>Configuration</Typography>
        <List sx={{ pt: 0 }}>
          {configItems.map(renderNavItem)}
        </List>
      </Box>

      {/* Upgrade Plan Box + User Actions */}
      <Box sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#059669', fontSize: '14px', fontWeight: 700 }}>
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#2b3674' }}>{user?.username || 'Admin'}</Typography>
              <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>Admin User</Typography>
            </Box>
          </Box>
          <IconButton onClick={handleLogout} size="small" sx={{ color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' } }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Upgrade Plan Box Removed as per request */}
      </Box>
    </Box>
  );

  return (
    <>
      <CommandPalette />
      <Box sx={{ display: 'flex', bgcolor: theme.palette.mode === 'dark' ? '#0b1437' : '#f4f7fe', minHeight: '100vh' }}>
        <CssBaseline />
        
        {/* Mobile Header (Only visible on small screens) */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, position: 'fixed', top: 0, left: 0, right: 0, height: 60, bgcolor: '#fff', zIndex: 1100, borderBottom: '1px solid #eee', px: 2, alignItems: 'center' }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#2b3674' }}>
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, color: '#2b3674', ml: 1 }}>Admin Panel</Typography>
        </Box>

        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none', borderRadius: '0 24px 24px 0' },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: location.pathname === '/dashboard' || location.pathname === '/' ? 0 : { xs: 2, md: 4 }, 
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: '60px', sm: 0 } 
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </>
  );
}

export default Layout;